import { Queue } from './queue';

export type IndexOfFn = (nodes: TreeNode[], searchNode: TreeNode) => number;

export type TraverseFn<T = any, S = any> = (node: TreeNode<T>) => boolean | void;

export class TreeNode<T = any> {
  protected id: string;
  protected data: T;
  protected parent: TreeNode = null;
  protected children: TreeNode[] = [];

  constructor(id: string, data?: T, children: TreeNode[] = []) {
    this.id = id;
    this.data = data;
    this.children = children;
  }

  getId(): string {
    return this.id;
  }

  setId(id: string) {
    this.id = id;
  }

  getData(): T {
    return this.data;
  }

  setData(data: T) {
    this.data = data;
  }

  getParent(): TreeNode {
    return this.parent;
  }

  setParent(parent: TreeNode) {
    this.parent = parent;
  }

  getChildren(): TreeNode[] {
    return this.children;
  }

  setChildren(children: TreeNode[]) {
    this.children = children;
  }

  clearChildren() {
    this.children.forEach(node => (node.parent = null));
    this.children.splice(0, this.children.length);
  }

  addChild(node: TreeNode) {
    if (node.parent != null) {
      node.parent.removeChild(node);
    }
    node.parent = this;
    this.children.push(node);
  }

  removeChild(node: TreeNode, indexOfFn?: IndexOfFn): boolean {
    let index = this.getIndex(node, indexOfFn);
    if (index > -1) {
      this.getChildren().splice(index, 1);
      return true;
    }
    return false;
  }

  protected getIndex(node: TreeNode, indexOfFn?: IndexOfFn): number {
    if (indexOfFn != null) {
      return indexOfFn(this.getChildren(), node);
    }
    return this.getChildren().indexOf(node, 0);
  }

  traverseDF(callback: TraverseFn<T>) {
    (function recurse(currentNode: TreeNode) {
      for (let child of currentNode.getChildren()) {
        recurse(child);
      }
      callback(currentNode);
    })(this);
  }

  traverseBF(callback: TraverseFn<T>) {
    let queue = new Queue<TreeNode>();

    queue.push(this);

    let currentNode = queue.pop();

    while (currentNode) {
      for (let child of currentNode.getChildren()) {
        queue.push(child);
      }

      if (callback(currentNode) === false) {
        break;
      }
      currentNode = queue.pop();
    }
  }
}

export class TreeNodeBuilder<T = any> {
  protected id: string;
  protected data: T;
  protected parent: TreeNode = null;
  protected children: TreeNode[] = [];

  withId(id: string): TreeNodeBuilder<T> {
    this.id = id;
    return this;
  }

  withData(data: T): TreeNodeBuilder<T> {
    this.data = data;
    return this;
  }

  withParent(parent: TreeNode): TreeNodeBuilder<T> {
    this.parent = parent;
    return this;
  }

  withChildren(children: TreeNode[]): TreeNodeBuilder<T> {
    this.children = children;
    return this;
  }

  build(): TreeNode<T> {
    const tn = new TreeNode<T>(null);
    tn.setId(this.id);
    tn.setData(this.data);
    tn.setChildren(this.children);
    tn.setParent(this.parent);
    return tn;
  }
}
